import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";

/**
 * Panduan — SOP per peran (MANDAT-PRAUAT-26SEP).
 *
 * Isi dari repo (backend/panduan/panduan.json); tiap kalimat punya SUMBER yang hanya Owner lihat. Staf hanya
 * menerima bagian yang DISAHKAN Owner. Isi berubah (keputusan baru) → bagian otomatis "Perlu ditinjau" dan hilang
 * dari staf sampai disahkan ulang. `?layar=/rute` (tombol "?" di header) menyaring bagian untuk layar itu.
 */
const JENIS = [
  ["alur", "Alur pesanan"],
  ["sop", "SOP peran"],
  ["aturan", "Aturan"],
  ["masalah", "Kalau terjadi masalah"],
];
const WARNA_STATUS = {
  DISAHKAN: "bg-success text-success-foreground",
  PERLU_DITINJAU: "bg-warning text-warning-foreground",
  DRAF: "bg-neutral text-neutral-foreground",
};
// Kode peran tidak tampil mentah ke staf (K200 butir 6).
const PERAN_LABEL = {
  SEMUA: "Semua peran", OWNER: "Owner", KEPALA_CABANG: "Kepala Cabang", CS: "CS",
  ADMIN_PRODUKSI: "Admin Produksi", ADMIN_GUDANG: "Admin Gudang", ADMIN_PURCHASING: "Admin Purchasing",
  FINANCE: "Finance", DESIGNER_EDITOR: "Desainer", SPV_DESAINER: "SPV Desainer", PEMANTAU: "Pemantau",
};
const namaPeran = (daftar) => (daftar || []).map((p) => PERAN_LABEL[p] || String(p).replace(/_/g, " ")).join(", ");
const LABEL_STATUS = { DISAHKAN: "Disahkan Owner", PERLU_DITINJAU: "Perlu ditinjau", DRAF: "Draf" };

function cocokLayar(b, layar) {
  if (!layar) return true;
  return (b.layar || []).some((r) => layar === r || layar.startsWith(r.endsWith("/") ? r : `${r}/`));
}

function BagianAlur({ b, peran, isOwner }) {
  return (
    <ol className="space-y-2" data-testid={`alur-${b.kode}`}>
      {(b.langkah || []).map((l) => {
        const milikku = peran && (l.peran || []).includes(peran);
        return (
          <li key={l.no} data-testid={milikku ? "langkah-milikku" : undefined}
              className={`rounded border p-3 text-sm ${milikku ? "border-primary bg-primary/10 font-medium" : ""}`}>
            <span className="mr-2 text-muted-foreground">{l.no}.</span>{l.judul}
            <span className="ml-2 text-xs text-muted-foreground">({namaPeran(l.peran)})</span>
            {milikku && <Badge className="ml-2">bagianmu di sini</Badge>}
            {isOwner && l.sumber && <div className="mt-1 text-xs text-muted-foreground">Sumber: {l.sumber}</div>}
          </li>
        );
      })}
    </ol>
  );
}

function KartuBagian({ b, peran, isOwner, onSahkan }) {
  return (
    <Card id={b.kode} className="p-5 space-y-3 scroll-mt-20" data-testid={`panduan-${b.kode}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">{b.judul}</h2>
        {isOwner && (
          <div className="flex items-center gap-2">
            <Badge className={WARNA_STATUS[b.status]} data-testid="status-panduan">{LABEL_STATUS[b.status]}</Badge>
            {b.status !== "DISAHKAN" && (
              <Button size="sm" onClick={() => onSahkan(b)} data-testid="btn-sahkan-panduan">Sahkan bagian ini</Button>
            )}
          </div>
        )}
      </div>
      {isOwner && (
        <div className="text-xs text-muted-foreground">Untuk: {namaPeran(b.peran)}</div>
      )}
      {b.jenis === "alur" && <BagianAlur b={b} peran={peran} isOwner={isOwner} />}
      <ul className="list-disc space-y-1.5 pl-5 text-sm">
        {(b.isi || []).map((x, i) => (
          <li key={i} className={x.teks?.startsWith("Belum ada ketentuan") ? "text-warning-soft-foreground" : ""}>
            {x.teks}
            {isOwner && x.sumber && <div className="text-xs text-muted-foreground">Sumber: {x.sumber}</div>}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export default function Panduan() {
  const { isOwner } = useAuth();
  const [params] = useSearchParams();
  const layar = params.get("layar") || "";
  const [data, setData] = useState(null);
  const [jenis, setJenis] = useState("alur");

  const muat = async () => {
    try { const r = await api.get("/panduan"); setData(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat panduan")); setData({ bagian: [] }); }
  };
  useEffect(() => { muat(); }, []);

  const bagian = useMemo(() => (data?.bagian || []).filter((b) => cocokLayar(b, layar)), [data, layar]);
  useEffect(() => {
    // Dari tombol "?": buka jenis pertama yang punya bagian untuk layar itu.
    if (layar && bagian.length && !bagian.some((b) => b.jenis === jenis)) setJenis(bagian[0].jenis);
  }, [layar, bagian, jenis]);
  // Dari "Sumber: Panduan › …" di Tanya ETAMS (K200): /panduan#KODE → buka tab jenisnya lalu gulir ke bagiannya.
  const kodeTuju = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
  useEffect(() => {
    if (!kodeTuju || !data) return;
    const b = (data.bagian || []).find((x) => x.kode === kodeTuju);
    if (b && b.jenis !== jenis) { setJenis(b.jenis); return; }
    if (b) document.getElementById(kodeTuju)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [kodeTuju, data, jenis]);

  const sahkan = async (b) => {
    try {
      await api.post(`/panduan/${b.kode}/sahkan`, { hash: b.hash });
      toast.success(`"${b.judul}" disahkan — staf kini melihatnya.`);
      muat();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal mengesahkan")); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Memuat panduan…</div>;
  const tampil = bagian.filter((b) => b.jenis === jenis);
  const belumSah = isOwner ? (data.bagian || []).filter((b) => b.status !== "DISAHKAN").length : 0;

  return (
    <div className="max-w-4xl space-y-4" data-testid="halaman-panduan">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Panduan</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {layar ? "Panduan untuk layar ini." : "Cara kerja ETAMSIOS untuk peranmu."}
          {isOwner && belumSah > 0 && <> {belumSah} bagian menunggu pengesahanmu — staf belum melihatnya.</>}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        {JENIS.map(([k, label]) => (
          <Button key={k} size="sm" variant={jenis === k ? "default" : "outline"} onClick={() => setJenis(k)}
                  data-testid={`tab-panduan-${k}`}>{label}</Button>
        ))}
      </div>
      {tampil.length === 0 && (
        <Card className="p-5 text-sm text-muted-foreground" data-testid="panduan-kosong">
          {layar ? "Belum ada panduan yang disahkan untuk layar ini." : "Belum ada panduan yang disahkan di bagian ini."}
        </Card>
      )}
      {tampil.map((b) => (
        <KartuBagian key={b.kode} b={b} peran={data.peran} isOwner={isOwner} onSahkan={sahkan} />
      ))}
    </div>
  );
}
